import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col rounded-lg border border-lightGray30 px-3 py-3 text-left md:px-4",
  "data-testid": "station-records-section"
}
const _hoisted_2 = { class: "flex flex-col gap-3" }
const _hoisted_3 = {
  class: "text-sm text-gray-500",
  "data-testid": "station-records-count"
}
const _hoisted_4 = {
  key: 0,
  class: "flex flex-col items-start gap-1 text-sm text-negative",
  "data-testid": "station-records-error"
}
const _hoisted_5 = ["data-testid"]
const _hoisted_6 = { class: "flex flex-wrap items-center gap-2 border-b border-gray-30 px-3 py-2 text-sm" }
const _hoisted_7 = { class: "font-bold" }
const _hoisted_8 = { class: "text-gray-500" }
const _hoisted_9 = { class: "grid grid-cols-2 gap-x-4 gap-y-2 px-3 py-2 text-xs md:grid-cols-4" }
const _hoisted_10 = { class: "flex flex-col" }
const _hoisted_11 = /*#__PURE__*/_createElementVNode("span", { class: "font-bold uppercase text-gray-500" }, "Pernas cobertas", -1)
const _hoisted_12 = { class: "flex flex-col" }
const _hoisted_13 = /*#__PURE__*/_createElementVNode("span", { class: "font-bold uppercase text-gray-500" }, "Check-in", -1)
const _hoisted_14 = { class: "flex flex-col" }
const _hoisted_15 = /*#__PURE__*/_createElementVNode("span", { class: "font-bold uppercase text-gray-500" }, "Check-out", -1)
const _hoisted_16 = { class: "flex flex-col" }
const _hoisted_17 = /*#__PURE__*/_createElementVNode("span", { class: "font-bold uppercase text-gray-500" }, "Vale no horário efetivo", -1)
const _hoisted_18 = ["data-testid"]
const _hoisted_19 = ["data-testid"]
const _hoisted_20 = { class: "flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-dashed border-gray-30 px-3 py-2 text-xs" }
const _hoisted_21 = ["data-testid", "aria-expanded", "onClick"]
const _hoisted_22 = ["data-testid", "onClick"]
const _hoisted_23 = ["data-testid"]
const _hoisted_24 = {
  key: 0,
  class: "text-sm text-gray-500"
}

import { computed, ref, watch } from 'vue'
import CollapsibleSection from './CollapsibleSection.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import type { LabelBadgeColor } from '@/components/designSystem/Badges/LabelBadge.vue'
import DsAttendanceHistoryCard from '@/components/designSystem/Cards/DsAttendanceHistoryCard.vue'
import Network from '@/Network'
import type {
  DoctorStationRecord,
  LegKey
} from '@/Network/Types/Responses/DsAttendanceLegOverwriteResponses'
import {
  DS_ATTENDANCE_STATUS_LABELS,
  DSAttendanceStatus
} from '@/constants/ds-attendance.constants'
import { pathForStatus } from '@/modules/company/Approvals/DsAttendance/utils/status-route-map'
import {
  LEG_LABELS,
  RECORD_KIND_LABELS,
  formatTimeInZone
} from '@/modules/company/Approvals/DsAttendance/utils/leg-overwrite'
import { useErrorToast } from '@/composables/useErrorToast'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/apiError'
import { mapAuditTrailToHistoryEvents, type HistoryEvent } from '@/utils/dsAttendanceHistory'

/** "Esta seção não existe para você" (sem acesso ou backend anterior), não "deu ruim". */

export default /*#__PURE__*/_defineComponent({
  __name: 'StationRecordsSection',
  props: {
    doctorStationId: {},
    touchTarget: { type: Boolean, default: false }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

/**
 * Spec 2026-09-24 §7.1/§8.3 (Q14) — "Registros do plantão": TODOS os registros (folha e
 * atividade) do plantão, quem vence cada perna no horário efetivo, fixação e sobrescrita
 * (com "vale pelo #Y" de `overwrittenBy`). Horários no `timezone` (offset) do envelope.
 * Convive com "Registros de Atividade da vaga" (anexos, solicitações, Enviar registro) — D-W4.
 */
const SILENT_ERROR_STATUSES = [401, 403, 404, 405, 501]
const LEGS: LegKey[] = ['CHECKIN', 'CHECKOUT']



const { notifyError } = useErrorToast()

const records = ref<DoctorStationRecord[]>([])
const timezone = ref<string | null>(null)
const hasLoaded = ref(false)
const loadError = ref<string | null>(null)
const openHistoryId = ref<number | null>(null)
const historyLoadingId = ref<number | null>(null)
const historyByRecord = ref<Record<number, HistoryEvent[]>>({})

const isVisible = computed(
  () => hasLoaded.value && (records.value.length > 0 || loadError.value !== null)
)

const countLabel = computed(() =>
  records.value.length === 1 ? '1 registro' : `${records.value.length} registros`
)

/** Só a resposta da última chamada vale — troca de plantão no meio do fetch não sobrescreve. */
let fetchSeq = 0

async function fetchRecords() {
  const seq = ++fetchSeq
  const doctorStationId = props.doctorStationId
  loadError.value = null
  if (!doctorStationId) {
    records.value = []
    hasLoaded.value = true
    return
  }
  try {
    const response = await Network.dsAttendance.getDoctorStationRecords(doctorStationId)
    if (seq !== fetchSeq) return
    timezone.value = response.timezone
    records.value = response.records
  } catch (error) {
    if (seq !== fetchSeq) return
    records.value = []
    if (SILENT_ERROR_STATUSES.includes(getApiErrorStatus(error) ?? 0)) {
      console.debug('[StationRecords] sem acesso ou rota ausente; seção omitida', error)
    } else {
      console.error('Erro ao listar os registros do plantão:', error)
      loadError.value = getApiErrorMessage(
        error,
        'Não foi possível carregar os registros deste plantão.'
      )
    }
  } finally {
    if (seq === fetchSeq) hasLoaded.value = true
  }
}

function kindLabel(record: Pick<DoctorStationRecord, 'kind'>): string {
  return RECORD_KIND_LABELS[record.kind] ?? record.kind
}

function statusLabel(record: DoctorStationRecord): string {
  return DS_ATTENDANCE_STATUS_LABELS[record.status as DSAttendanceStatus] ?? record.status
}

function statusColor(record: DoctorStationRecord): LabelBadgeColor {
  return record.status === DSAttendanceStatus.APPROVED ? 'green' : 'orange'
}

/** FOLHA cobre sempre as duas pernas; `coveredLegs` nulo = as duas (§3). */
function coveredLegsOf(record: DoctorStationRecord): LegKey[] {
  if (record.kind === 'FOLHA' || !record.coveredLegs || record.coveredLegs === 'BOTH') return LEGS
  return [record.coveredLegs]
}

function coveredLabel(record: DoctorStationRecord): string {
  const legs = coveredLegsOf(record)
  return legs.length === 2 ? 'Ambas' : LEG_LABELS[legs[0]]
}

function timeLabel(iso: string | null): string {
  return formatTimeInZone(iso, timezone.value) ?? '—'
}

function winsLabel(record: DoctorStationRecord): string {
  if (!record.winsLegs.length) return 'Nenhuma perna'
  return record.winsLegs
    .map((leg) => `${LEG_LABELS[leg]}${record.pinnedLegs.includes(leg) ? ' (fixada)' : ''}`)
    .join(' · ')
}

function overwriterLabel(containerId: number): string {
  const overwriter = records.value.find((other) => other.id === containerId)
  return overwriter
    ? `pelo ${kindLabel(overwriter)} #${containerId}`
    : `pelo registro #${containerId}`
}

/**
 * Q6 — "sobrescrito por #Y", direto de `overwrittenBy` (§7.1). Perna coberta, perdida e sem
 * entrada em `overwrittenBy` foi vencida por fonte fora dos registros (app/permanência).
 */
function lostLegsLabel(record: DoctorStationRecord): string | null {
  if (record.status !== DSAttendanceStatus.APPROVED) return null
  const lost = coveredLegsOf(record).filter((leg) => !record.winsLegs.includes(leg))
  if (!lost.length) return null
  const byOverwriter = new Map<string, LegKey[]>()
  for (const leg of lost) {
    const entry = (record.overwrittenBy ?? []).find((item) => item.leg === leg)
    const key = entry ? overwriterLabel(entry.containerId) : 'por outra fonte do plantão'
    byOverwriter.set(key, [...(byOverwriter.get(key) ?? []), leg])
  }
  return [...byOverwriter.entries()]
    .map(([who, legs]) => {
      const names = legs.map((leg) => LEG_LABELS[leg]).join(' e ')
      return `${names} ${legs.length > 1 ? 'valem' : 'vale'} ${who}`
    })
    .join(' · ')
}

async function toggleHistory(recordId: number) {
  if (openHistoryId.value === recordId) {
    openHistoryId.value = null
    return
  }
  openHistoryId.value = recordId
  if (historyByRecord.value[recordId]) return
  historyLoadingId.value = recordId
  try {
    const response = await Network.dsAttendance.getAuditTrail(recordId)
    historyByRecord.value = {
      ...historyByRecord.value,
      [recordId]: mapAuditTrailToHistoryEvents(response.data)
    }
  } catch (error) {
    notifyError(error, 'Não foi possível carregar o histórico do registro.')
  } finally {
    historyLoadingId.value = null
  }
}

/** Nova aba — o gestor não perde o plantão que está analisando. */
function openRecord(record: DoctorStationRecord) {
  window.open(pathForStatus(record.id, record.status), '_blank', 'noopener,noreferrer')
}

watch(
  () => props.doctorStationId,
  () => {
    hasLoaded.value = false
    records.value = []
    timezone.value = null
    openHistoryId.value = null
    historyByRecord.value = {}
    fetchRecords()
  },
  { immediate: true }
)

__expose({ fetchRecords })

return (_ctx: any,_cache: any) => {
  return (isVisible.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(CollapsibleSection, {
          title: "Registros do plantão",
          "test-id": "station-records-accordion",
          "touch-target": _ctx.touchTarget,
          "expand-label": "Ver registros do plantão",
          "collapse-label": "Ocultar registros do plantão"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("span", _hoisted_3, _toDisplayString(countLabel.value), 1),
              (loadError.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createElementVNode("span", null, _toDisplayString(loadError.value), 1),
                    _createElementVNode("button", {
                      type: "button",
                      class: "font-semibold underline",
                      "data-testid": "station-records-retry-button",
                      onClick: fetchRecords
                    }, " Tentar novamente ")
                  ]))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(records.value, (record) => {
                return (_openBlock(), _createElementBlock("article", {
                  key: record.id,
                  class: "rounded border border-gray-30 bg-white",
                  "data-testid": `station-record-card-${record.id}`
                }, [
                  _createElementVNode("header", _hoisted_6, [
                    _createElementVNode("span", _hoisted_7, _toDisplayString(kindLabel(record)), 1),
                    _createElementVNode("span", _hoisted_8, "#" + _toDisplayString(record.id), 1),
                    _createVNode(LabelBadge, {
                      label: statusLabel(record),
                      color: statusColor(record),
                      size: "small",
                      icon: null
                    }, null, 8, ["label", "color"]),
                    (record.overwritten !== 'NONE')
                      ? (_openBlock(), _createBlock(LabelBadge, {
                          key: 0,
                          label: record.overwritten === 'TOTAL' ? 'Sobrescrito' : 'Sobrescrito parcialmente',
                          color: "yellow",
                          size: "small",
                          icon: null,
                          "data-testid": `station-record-overwritten-${record.id}`
                        }, null, 8, ["label", "data-testid"]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_9, [
                    _createElementVNode("div", _hoisted_10, [
                      _hoisted_11,
                      _createElementVNode("span", null, _toDisplayString(coveredLabel(record)), 1)
                    ]),
                    _createElementVNode("div", _hoisted_12, [
                      _hoisted_13,
                      _createElementVNode("span", null, _toDisplayString(timeLabel(record.checkin)), 1)
                    ]),
                    _createElementVNode("div", _hoisted_14, [
                      _hoisted_15,
                      _createElementVNode("span", null, _toDisplayString(timeLabel(record.checkout)), 1)
                    ]),
                    _createElementVNode("div", _hoisted_16, [
                      _hoisted_17,
                      _createElementVNode("span", {
                        "data-testid": `station-record-wins-${record.id}`
                      }, _toDisplayString(winsLabel(record)), 9, _hoisted_18)
                    ])
                  ]),
                  (lostLegsLabel(record))
                    ? (_openBlock(), _createElementBlock("p", {
                        key: 0,
                        class: "px-3 pb-2 text-xs text-gray-500",
                        "data-testid": `station-record-lost-${record.id}`
                      }, _toDisplayString(lostLegsLabel(record)), 9, _hoisted_19))
                    : _createCommentVNode("", true),
                  _createElementVNode("footer", _hoisted_20, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "font-semibold text-primary30 underline",
                      "data-testid": `station-record-history-toggle-${record.id}-button`,
                      "aria-expanded": openHistoryId.value === record.id ? 'true' : 'false',
                      onClick: ($event: any) => (toggleHistory(record.id))
                    }, _toDisplayString(openHistoryId.value === record.id ? 'Ocultar histórico' : 'Ver histórico'), 9, _hoisted_21),
                    _createElementVNode("button", {
                      type: "button",
                      class: "font-semibold text-primary30 underline",
                      "data-testid": `station-record-open-${record.id}-button`,
                      onClick: ($event: any) => (openRecord(record))
                    }, " Abrir registro ", 8, _hoisted_22)
                  ]),
                  (openHistoryId.value === record.id)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        class: "px-3 pb-3",
                        "data-testid": `station-record-history-${record.id}`
                      }, [
                        (historyLoadingId.value === record.id)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_24, " Carregando histórico… "))
                          : (_openBlock(), _createBlock(DsAttendanceHistoryCard, {
                              key: 1,
                              title: "Histórico do registro",
                              events: historyByRecord.value[record.id] ?? []
                            }, null, 8, ["events"]))
                      ], 8, _hoisted_23))
                    : _createCommentVNode("", true)
                ], 8, _hoisted_5))
              }), 128))
            ])
          ]),
          _: 1
        }, 8, ["touch-target"])
      ]))
    : _createCommentVNode("", true)
}
}

})